import store from '@/store'
import {
  connectionService,
  utils,
  USER_UPDATE_GROUPS_URI,
  PERSON_DELETE_ROL_URI,
  RACE_TIMEOUT,
  PERSON_ADD_ROL_URI,
  PERSON_UPDATE_ROL_URI,
  PERSON_UPDATE_URI,
  PERSON_DELETE_URI,
  PERSON_FETCH_URI,
  PERSON_FIND_URI
} from '@leapsight/styleguide'
import { i18n } from '@leapsight/portal'

export default {
  keyGroups: Object.freeze({
    1: 'business_account_user',
    2: 'business_account_admin',
    3: 'business_account_fleet_admin'
  }),
  keyRols: Object.freeze({
    1: 'mrn:party_role:user',
    3: 'mrn:party_role:emergency_contact',
    4: 'mrn:party_role:driver'
  }),
  ROL_ID: Object.freeze({
    user: 1,
    contact: 2,
    emergencyContact: 3,
    driver: 4
  }),
  PROFILE_ID: Object.freeze({
    user: 1,
    emergencyContact: 3,
    driver: 4
  }),
  getPersonId(person) {
    const input = person
    const fields = input.id.split(':')
    const personId = fields[2]
    return personId
  },
  getPersonEmail(person) {
    let emails
    const hasEmail = person.hasOwnProperty('emails')
    if (hasEmail) {
      emails = person.emails
    }
    return emails
  },
  getConnection() {
    return store.getters['auth/connection']
  },
  getPhones(person) {
    const { phones } = person
    const { roles } = person

    if (!Boolean(roles)) return []

    if (!('mrn:party_role:emergency_contact' in roles)) return []

    const phonesObject = roles['mrn:party_role:emergency_contact'].phones.map(
      phone => {
        // por defecto el tipo de telénfono es mobile
        const { tag } = phones.find(pho => pho.number == phone.number) ?? {
          tag: 'mobile'
        }
        const { index, number } = phone
        return {
          index,
          phone: {
            number,
            tag
          }
        }
      }
    )

    return phonesObject
  },
  getRoles(person) {
    //check if person has any rol
    const hasRole = person.hasOwnProperty('roles')
    const allRoles = []

    if (hasRole) {
      //get object of all roles
      const roles = person.roles

      //get key for each rol
      const keyRoles = Object.keys(roles)

      //removes de 'mrn:party_role:' from the string
      keyRoles.forEach(role => {
        const fields = role.split(':')
        const oneRole = fields[fields.length - 1]
        allRoles.push(oneRole)
      })
    }

    return allRoles
  },
  getProfile(person) {
    const hasRole = person.hasOwnProperty('roles')
    let hasUserProfile = false

    if (hasRole)
      hasUserProfile = person.roles.hasOwnProperty('mrn:party_role:user')

    const allProfiles = []
    if (hasRole && hasUserProfile) {
      //get object of all roles
      const roles = person.roles
      //get key for each rol
      const keyRoles = Object.keys(roles)

      //make action for each role the person has
      for (let i = 0; i < keyRoles.length; i++) {
        const element = keyRoles[i]
        const fields = element.split(':')
        const splitRole = fields[fields.length - 1]
        const eachProfileGroups = roles[element].groups
        const profileByRole = {
          [splitRole]: eachProfileGroups
        }
        allProfiles.push(profileByRole)
      }
    }
    return allProfiles
  },
  getIbutton(person) {
    const { roles } = person
    let ib = null
    if (Boolean(roles) && 'mrn:party_role:driver' in roles) {
      const { ibutton } = roles['mrn:party_role:driver']
      ib = ibutton ?? ''
    }
    return ib
  },
  async getPersonsList(projection = '') {
    return new Promise((resolve, reject) => {
      const p1 = new Promise((resolve, reject) => {
        setTimeout(resolve, 5000, [])
      })
      // personListRace is a async function that fetches the person list
      const p2 = this.personListRace(projection)
      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  personListRace(projection) {
    return new Promise((resolve, reject) => {
      const accountId = connectionService.getAccountId()
      const connection = store.getters['auth/connection']
      const args = [{ account_id: accountId }]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security,
        query_spec: {
          _p: projection
        }
      }
      connection.session
        .call(PERSON_FIND_URI, args, kwArgs, {})
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            reject(wampError)
          }
        )
        .catch(err => {
          console.error(err)
        })
    })
  },

  thingAssignmentsRemove(userId, things) {
    const thingIds = things.map(thing => thing.uriId)
    return new Promise((resolve, reject) => {
      const accountId = connectionService.getAccountId()
      const connection = store.getters['auth/connection']
      const args = [accountId, userId, thingIds]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security
      }
      connection.session
        .call('com.magenta.thing.assignments.remove', args, kwArgs, {})
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            reject(wampError)
          }
        )
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },

  personFetch({ userId }) {
    return new Promise((resolve, reject) => {
      const kwArgs = connectionService.createkwArgs()
      const connection = store.getters['auth/connection']
      connection.session
        .call(PERSON_FETCH_URI, [userId], kwArgs, {})
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            reject(wampError)
          }
        )
        .catch(err => {
          console.error(err)
        })
    })
  },

  thingAssignmentsGet(userId) {
    return new Promise((resolve, reject) => {
      const accountId = connectionService.getAccountId()
      const connection = store.getters['auth/connection']
      const args = [accountId, userId]
      const security = connectionService.createkwArgs()
      const querySpec = { query_spec: { _p: 'info.label,id' } }
      const kwArgs = {
        ...querySpec,
        ...security
      }
      connection.session
        .call('com.magenta.thing.assignments.get', args, kwArgs, {})
        .then(
          response => {
            resolve(response.args[0])
          },
          wampError => {
            reject(wampError)
          }
        )
        .catch(err => {
          console.error(err)
        })
    })
  },

  thingAssignmentsAdd(userId, things) {
    const thingIds = things.map(thing => thing.uriId)
    return new Promise((resolve, reject) => {
      const accountId = connectionService.getAccountId()
      const connection = store.getters['auth/connection']
      const args = [accountId, userId, thingIds]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security
      }
      connection.session
        .call('com.magenta.thing.assignments.add', args, kwArgs, {})
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            reject(wampError)
          }
        )
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },

  createPerson(person) {
    return new Promise((resolve, reject) => {
      const accountId = connectionService.getAccountId()
      const connection = store.getters['auth/connection']
      const args = [accountId, person]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security
      }
      connection.session
        .call('com.magenta.person.create', args, kwArgs, {})
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            reject(wampError)
          }
        )
        .catch(err => {
          console.error(err)
        })
    })
  },

  prepareAddressForStore() {
    return {
      address_line1: '',
      city: '',
      state: '',
      country: '',
      postal_code: ''
    }
  },

  prepareEmailForStore({ email, tag }) {
    return [
      {
        email,
        tag
      }
    ]
  },

  preparePhoneForStore({ phones }) {
    return phones.map(phone => {
      const { number, type } = phone
      return {
        number,
        tag: type
      }
    })
  },

  prepareRolesForStore({ rol, email, phonesContact, ibutton }) {
    const roles = {}

    const rk = rol
      .filter(r => Boolean(this.keyRols[r.rol]))
      .map(r => this.keyRols[r.rol])

    const groups = rol
      .filter(r => Boolean(this.keyGroups[r.profile]))
      .map(r => this.keyGroups[r.profile])

    if (rk.includes('mrn:party_role:user')) {
      roles['mrn:party_role:user'] = {
        username: email,
        groups
      }
    }

    if (rk.includes('mrn:party_role:emergency_contact')) {
      const phones = phonesContact.map(phone => {
        const { index, number } = phone
        return {
          index: String(index),
          number
        }
      })

      roles['mrn:party_role:emergency_contact'] = {
        phones
      }
    }

    if (rk.includes('mrn:party_role:driver')) {
      roles['mrn:party_role:driver'] = {
        ...(Boolean(ibutton) ? { ibutton } : undefined)
      }
    }

    return roles
  },

  preparePersonalDataForStore({ identificationType, identificationNumber }) {
    if (identificationType === 'DNI' && identificationNumber?.length < 8)
      return `0${identificationNumber}`

    return identificationNumber
  },

  prepareForStore(payload) {
    const rol = payload.rol.map(e => e)
    const [phonesContact] = payload.rol
      .filter(rol => rol.profile === this.PROFILE_ID.emergencyContact)
      .map(contact => contact.rolContact)

    const ibutton = payload.rol.find(r => r.rol === this.ROL_ID.driver)?.ibutton

    const roles = this.prepareRolesForStore({
      rol,
      email: payload.email,
      phonesContact,
      ibutton
    })

    const phones =
      Boolean(phonesContact) &&
      this.preparePhoneForStore({ phones: phonesContact })

    const person = {
      first_name: payload.firstName,
      last_name: payload.lastName,
      address: this.prepareAddressForStore(),
      emails: this.prepareEmailForStore({ email: payload.email, tag: 'work' })
    }

    const { identificationType, identificationNumber } = payload

    Boolean(identificationType)
      ? (person.identification_type = identificationType)
      : null
    Boolean(identificationNumber)
      ? (person.identification_number = this.preparePersonalDataForStore({
          identificationType,
          identificationNumber
        }))
      : null
    Boolean(phones) ? (person.phones = phones) : null
    Boolean(Object.keys(roles).length) ? (person.roles = roles) : null

    return person
  },

  prepareForUpdateAddRole({ email, profiles, phonesIn, ibutton }) {
    const userBody = {
      username: email,
      groups: [profiles]
    }

    const phones = phonesIn?.map(p => {
      const { phone } = p
      return {
        number: phone.number,
        index:
          phone.index !== null && phone.index !== undefined
            ? String(phone.index)
            : p.index
      }
    })

    const objForStore = {
      ...(Boolean(profiles) && {
        'mrn:party_role:user': userBody
      }),
      ...(Boolean(phones?.length) && {
        'mrn:party_role:emergency_contact': {
          phones
        }
      }),
      ...(ibutton != null &&
        (ibutton === ''
          ? {
              'mrn:party_role:driver': {}
            }
          : {
              'mrn:party_role:driver': { ibutton }
            }))
    }

    return objForStore
  },

  addRolPerson(id, { email, profiles, phones, ibutton }) {
    const roleAddBody = this.prepareForUpdateAddRole({
      email: email,
      profiles: profiles,
      phonesIn: phones,
      ibutton
    })
    return new Promise((resolve, reject) => {
      const connection = store.getters['auth/connection']
      const args = [id, roleAddBody]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security
      }
      const options = {}

      connection.session
        .call(PERSON_ADD_ROL_URI, args, kwArgs, options)
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            console.error(wampError)
            reject(wampError)
          }
        )
        .catch(error => {
          console.error('catch error')
        })
    })
  },
  updatePersonRol(id, { email, profile, phonesIn, ibutton }) {
    const prepareForUpdateRole = this.prepareForUpdateAddRole({
      email,
      profile,
      phonesIn,
      ibutton
    })
    return new Promise((resolve, reject) => {
      const connection = store.getters['auth/connection']
      const args = [id, prepareForUpdateRole]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security
      }
      const options = {}

      connection.session
        .call(PERSON_UPDATE_ROL_URI, args, kwArgs, options)
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            console.error(wampError)
            reject(wampError)
          }
        )
        .catch(error => {
          console.error('catch error')
        })
    })
  },
  updateProfilePerson(id, { profile }) {
    let person = { roles: {} }
    person = {
      groups: [profile]
    }
    return new Promise((resolve, reject) => {
      const connection = store.getters['auth/connection']
      const args = [id, person.groups]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security
      }
      const options = {}

      connection.session
        .call(USER_UPDATE_GROUPS_URI, args, kwArgs, options)
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            console.error(wampError)
            reject(wampError)
          }
        )
        .catch(error => {
          console.error('catch error')
        })
    })
  },
  removeRolPerson(id, rol) {
    return new Promise((resolve, reject) => {
      const connection = store.getters['auth/connection']
      const args = [id, rol]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security
      }
      const options = {}

      connection.session
        .call(PERSON_DELETE_ROL_URI, args, kwArgs, options)
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            console.error(wampError)
            reject(wampError)
          }
        )
        .catch(error => {
          console.error('catch error')
        })
    })
  },

  preparePersonForUpdate({
    firstName,
    lastName,
    identifyType,
    identifyNumber,
    phonesIn
  }) {
    const phonesPerson = phonesIn
      .filter(ph => ph.phone.number !== '')
      .map(ph => {
        const { phone } = ph
        const { number, tag } = phone
        return {
          number,
          tag
        }
      })

    const objForUpdate = {
      first_name: firstName,
      last_name: lastName,
      identification_type: identifyType,
      identification_number: identifyNumber,
      phones: Boolean(phonesPerson.length) ? phonesPerson : undefined
    }

    return objForUpdate
  },
  updatePerson(
    id,
    { firstName, lastName, identifyType, identifyNumber, phonesIn }
  ) {
    const objPersonForUpdate = this.preparePersonForUpdate({
      firstName,
      lastName,
      identifyType,
      identifyNumber,
      phonesIn
    })
    return new Promise((resolve, reject) => {
      const connection = store.getters['auth/connection']
      const args = [id, objPersonForUpdate]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security
      }
      const options = {}

      connection.session
        .call(PERSON_UPDATE_URI, args, kwArgs, options)
        .then(
          response => {
            resolve(response)
          },
          wampError => {
            console.error(wampError)
            reject(wampError)
          }
        )
        .catch(error => {
          console.error(error)
        })
    })
  },
  deletePerson(id) {
    return new Promise((resolve, reject) => {
      const connection = this.getConnection()
      const args = [id]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security
      }
      const p1 = new Promise((resolve, reject) => {
        setTimeout(reject, RACE_TIMEOUT, [])
      })
      const p2 = new Promise((resolve, reject) => {
        connection.session
          .call(PERSON_DELETE_URI, args, kwArgs, {})
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
      })

      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  //ERRORS
  getErrorMessageByCode(code = '') {
    let strToReturn
    switch (code) {
      case 'com.magenta.error.unexpected_error':
        strToReturn = i18n.t('general.dialogComponent.text_error')
        break
      default:
        strToReturn = i18n.t('general.dialogComponent.text_error')
        break
    }
    return strToReturn
  }
}
